import React, { useState, useEffect } from 'react';
import { MessageSquare, Send } from 'lucide-react';
import { useMessages } from '../../../hooks/useMessages';
import { useAPI } from '../../../hooks/useAPI';

const MessagesPage = ({ user }) => {
  const { conversations, loadConversations } = useMessages();
  const { apiCall } = useAPI();
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const loadMessages = async (friendId) => {
    setLoading(true);
    try {
      const data = await apiCall(`/messages/conversation/${friendId}`);
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error loading messages:', err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedFriend) return;

    try {
      const message = await apiCall('/messages', {
        method: 'POST',
        body: JSON.stringify({
          receiver_id: selectedFriend.id,
          content: newMessage
        })
      });
      
      setMessages([...messages, message]);
      setNewMessage('');
      loadConversations();
    } catch (err) {
      alert(err.message);
    }
  };

  useEffect(() => {
    if (selectedFriend) {
      loadMessages(selectedFriend.id);
    }
  }, [selectedFriend]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden" style={{ height: '70vh' }}>
        <div className="flex h-full">
          {/* Conversations List */}
          <div className="w-1/3 border-r flex flex-col">
            <div className="p-4 border-b bg-indigo-50">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <MessageSquare size={24} className="text-indigo-500" />
                Messages
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare size={48} className="mx-auto mb-4 text-gray-400" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-2">Add friends to start chatting!</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.friend.id}
                    onClick={() => setSelectedFriend(conv.friend)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition ${
                      selectedFriend?.id === conv.friend.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {conv.friend.username[0].toUpperCase()}
                        </div>
                        {conv.unread_count > 0 && (
                          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {conv.unread_count}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{conv.friend.username}</p>
                        {conv.last_message && (
                          <p className="text-sm text-gray-500 truncate">
                            {conv.last_message.sender_id === user.id ? 'You: ' : ''}
                            {conv.last_message.content}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col">
            {selectedFriend ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                      {selectedFriend.username[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">{selectedFriend.username}</p>
                      <p className="text-xs text-gray-500">{selectedFriend.email}</p>
                    </div>
                  </div>
                </div>

                {/* Messages Display */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-4 py-2 rounded-lg ${
                            msg.sender_id === user.id
                              ? 'bg-indigo-500 text-white'
                              : 'bg-gray-200 text-gray-800'
                          }`}
                        >
                          <p className="break-words">{msg.content}</p>
                          <p
                            className={`text-xs mt-1 ${
                              msg.sender_id === user.id ? 'text-indigo-100' : 'text-gray-500'
                            }`}
                          >
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-4 border-t bg-gray-50">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="px-6 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition font-semibold disabled:opacity-50"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageSquare size={64} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-semibold">Select a conversation</p>
                  <p className="text-sm">Choose a friend to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;